import React, { FunctionComponent, useCallback } from 'react';
import FormField from 'components/form-field/form-field';
import { useFormikContext } from 'formik';
import { CompanyFormFields } from 'modules/company/types/companyFormFields.type';
import { CountryCode } from 'modules/company/types/company.type';
import { orderLegalForms } from 'modules/company/utils/orderLegalForms';
import { useGetLegalForms } from 'generated/api-client/@tanstack/react-query.gen';
import { DropdownProps } from 'semantic-ui-react';

type Props = {
  isDisabled: boolean;
};

const LEGAL_FORM_FIELD_NAME = 'legalForm';
const LEGAL_FORM_LABEL = "Statut de l'entreprise";
const LEGAL_FORM_HELP_TEXT = 'Nécessaire pour la signature du contrat';
const BE_LEGAL_FORM_PLACEHOLDER = 'Ex: SAS, SARL, EURL, etc.';
const FR_LEGAL_FORM_PLACEHOLDER = 'Sélectionnez le statut';

export const LegalFormField: FunctionComponent<Props> = ({ isDisabled }) => {
  const { values, setFieldValue } = useFormikContext<CompanyFormFields>();

  const { data: legalForms } = useGetLegalForms();

  const handleLegalFormChange = useCallback(
    (event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const selectedValue = data.value as string;
      const currentName = values.name ?? '';

      const isIndividual =
        legalForms
          ?.find((legalForm) => legalForm.label === selectedValue)
          ?.code.startsWith('1') ?? false;

      if (isIndividual && !currentName.includes('(EI)')) {
        setFieldValue('name', currentName.trimEnd() + ' (EI)');
      }

      if (!isIndividual && currentName.includes('(EI)')) {
        setFieldValue('name', currentName.replace(/\s*\(EI\)\s*/g, ' ').trim());
      }
    },
    [values.name, setFieldValue, legalForms],
  );

  switch (values.countryCode) {
    case CountryCode.BE:
      return (
        <FormField
          type="text"
          name={LEGAL_FORM_FIELD_NAME}
          label={LEGAL_FORM_LABEL}
          helpText={LEGAL_FORM_HELP_TEXT}
          placeholder={BE_LEGAL_FORM_PLACEHOLDER}
          disabled={isDisabled}
          required
        />
      );
    case CountryCode.FR:
      return (
        <FormField
          type="select"
          search
          selection
          name={LEGAL_FORM_FIELD_NAME}
          label={LEGAL_FORM_LABEL}
          helpText={LEGAL_FORM_HELP_TEXT}
          placeholder={FR_LEGAL_FORM_PLACEHOLDER}
          disabled={isDisabled}
          required
          onChange={handleLegalFormChange}
          options={orderLegalForms(legalForms ?? []).map(({ code, label }) => ({
            key: code,
            value: label,
            text: label,
          }))}
        />
      );
  }
};
